import React from 'react';
import { aboutData } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, Settings, Wrench, Users, Target } from 'lucide-react';

const AboutSection = () => {
  const iconMap = {
    0: Settings,
    1: Wrench, 
    2: Users,
    3: Target
  };

  return (
    <section id="about" className="py-10 bg-gradient-to-b from-gray-50/50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#0a8ec5]/10 border border-[#0a8ec5]/20 rounded-full px-4 py-2 mb-6">
            <span className="text-[#0a8ec5] font-semibold text-sm">About Our Company</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {aboutData.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {aboutData.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Company Description */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {aboutData.description1}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {aboutData.description2}
              </p>
              
              {/* Services List */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 mb-4">Our Core Specializations:</h4>
                {aboutData.services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#0a8ec5] flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>

              {/* Company Highlights */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex flex-wrap gap-3">
                  <Badge variant="outline" className="border-[#0a8ec5] text-[#0a8ec5] px-3 py-1">
                    MSME Registered
                  </Badge>
                  <Badge variant="outline" className="border-[#cd2d31] text-[#cd2d31] px-3 py-1">
                    Global Reach
                  </Badge>
                  <Badge variant="outline" className="border-[#0a8ec5] text-[#0a8ec5] px-3 py-1">
                    20+ Years Experience
                  </Badge>
                  <Badge variant="outline" className="border-[#cd2d31] text-[#cd2d31] px-3 py-1">
                    Turn-key Solutions
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Expertise Cards */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Our Expertise Areas</h3>
            {aboutData.expertise.map((item, index) => {
              const IconComponent = iconMap[index];
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:scale-105 border-l-4 border-l-[#0a8ec5]">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#0a8ec5]/10 p-3 rounded-lg group-hover:bg-[#0a8ec5]/20 transition-colors">
                        <IconComponent className="w-6 h-6 text-[#0a8ec5]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-[#0a8ec5] transition-colors">
                          {item.title}
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Company Stats */}
        <div className="mt-16 bg-gradient-to-r from-[#0a8ec5]/5 to-[#cd2d31]/5 rounded-2xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-3xl font-bold text-[#0a8ec5] mb-2">100+</div>
              <div className="text-gray-600 font-medium">Projects Completed</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-3xl font-bold text-[#cd2d31] mb-2">50+</div>
              <div className="text-gray-600 font-medium">Enterprise Clients</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-3xl font-bold text-[#0a8ec5] mb-2">5+</div>
              <div className="text-gray-600 font-medium">Countries Served</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="text-3xl font-bold text-[#cd2d31] mb-2">99.9%</div>
              <div className="text-gray-600 font-medium">Efficiency Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;